import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, Link as RouterLink, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, LogOut, Shield, User, ShoppingBag, Heart, Home, Menu, Settings, Image, Users, MessageSquare, HelpCircle, Wrench, Bell, Megaphone, Palette, Mail, Send, MessageCircle, Search, Bot, Monitor, FileText, Accessibility } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import OrderHistory from "@/components/OrderHistory";
import FavoritesSection from "@/components/FavoritesSection";
import { UserRepairOrders } from "@/components/UserRepairOrders";

// Lazy load admin components to reduce initial bundle size
const AdminContactMessages = lazy(() => import("@/components/AdminContactMessages"));
const AdminMenuSettings = lazy(() => import("@/components/AdminMenuSettings").then(m => ({ default: m.AdminMenuSettings })));
const AdminSliderSettings = lazy(() => import("@/components/AdminSliderSettings").then(m => ({ default: m.AdminSliderSettings })));
const AdminUserManagement = lazy(() => import("@/components/AdminUserManagement").then(m => ({ default: m.AdminUserManagement })));
const AdminAllFAQSettings = lazy(() => import("@/components/AdminAllFAQSettings").then(m => ({ default: m.AdminAllFAQSettings })));
const AdminRepairManagement = lazy(() => import("@/components/AdminRepairManagement").then(m => ({ default: m.AdminRepairManagement })));
const AdminPromoBannerSettings = lazy(() => import("@/components/AdminPromoBannerSettings").then(m => ({ default: m.AdminPromoBannerSettings })));
const AdminColorHelper = lazy(() => import("@/components/AdminColorHelper").then(m => ({ default: m.AdminColorHelper })));
const AdminCustomerMessages = lazy(() => import("@/components/AdminCustomerMessages").then(m => ({ default: m.AdminCustomerMessages })));
const UserMessages = lazy(() => import("@/components/UserMessages").then(m => ({ default: m.UserMessages })));
const AdminSmsTemplates = lazy(() => import("@/components/AdminSmsTemplates").then(m => ({ default: m.AdminSmsTemplates })));
const AdminSearchSettings = lazy(() => import("@/components/AdminSearchSettings").then(m => ({ default: m.AdminSearchSettings })));
const AdminBotSettings = lazy(() => import("@/components/AdminBotSettings").then(m => ({ default: m.AdminBotSettings })));
const AdminTVDisplaySettings = lazy(() => import("@/components/AdminTVDisplaySettings").then(m => ({ default: m.AdminTVDisplaySettings })));
const AdminBlogManagement = lazy(() => import("@/components/AdminBlogManagement").then(m => ({ default: m.AdminBlogManagement })));
const AdminAccessibilityDashboard = lazy(() => import("@/components/AdminAccessibilityDashboard").then(m => ({ default: m.AdminAccessibilityDashboard })));
import { Sidebar, SidebarBody } from "@/components/ui/sidebar-aceternity";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ProfileTour } from "@/components/ProfileTour";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileProfileNav } from "@/components/MobileProfileNav";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  shipping_address: any;
  avatar_url: string | null;
  shopify_customer_id: string | null;
  birth_date: string | null;
  preferences: any;
}

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  type TabType = "details" | "orders" | "favorites" | "repairs" | "messages" | "admin" | "settings" | "slider" | "users" | "faq" | "admin-repairs" | "promo-banner" | "color-helper" | "customer-messages" | "sms-templates" | "search-settings" | "bot-settings" | "tv-display" | "blog-management" | "accessibility";
  
  // Get initial tab from URL query parameter
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get("tab");
    const validTabs = ["details", "orders", "favorites", "repairs", "messages", "admin", "settings", "slider", "users", "faq", "admin-repairs", "promo-banner", "color-helper", "customer-messages", "sms-templates", "search-settings", "bot-settings", "tv-display", "blog-management", "accessibility"];
    if (tabParam && validTabs.includes(tabParam)) {
      return tabParam as TabType;
    }
    return "details";
  };
  
  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());
  const [showTour, setShowTour] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [newRepairsCount, setNewRepairsCount] = useState(0);
  const [unreadUserMessagesCount, setUnreadUserMessagesCount] = useState(0);
  const isMobile = useIsMobile();
  
  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  
  // Password change fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Save the intended destination before redirecting to auth
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('redirectAfterLogin', currentPath);
        navigate("/auth");
        return;
      }

      setUserEmail(session.user.email || "");
      await loadProfile(session.user.id);
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('redirectAfterLogin', currentPath);
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const loadProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;

      setProfile(data);
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setPhone(data.phone || "");
      setBirthDate(data.birth_date || "");
      
      if (data.shipping_address && typeof data.shipping_address === 'object') {
        const address = data.shipping_address as { street?: string; city?: string; zip_code?: string };
        setStreetAddress(address.street || "");
        setCity(address.city || "");
        setZipCode(address.zip_code || "");
      }

      // Check if user is admin
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      setIsAdmin(!!roleData);

      // Fetch new messages count for admin
      if (roleData) {
        const { count } = await supabase
          .from("contact_submissions")
          .select("*", { count: 'exact', head: true })
          .eq("status", "new");
        setNewMessagesCount(count || 0);
      }

      // Fetch unread repair updates for user
      const prefs = data.preferences as Record<string, any> | null;
      const repairsLastViewed = prefs?.repairs_last_viewed_at;
      const { data: repairUpdates, error: repairsError } = await supabase
        .from("repair_orders")
        .select("updated_at")
        .eq("user_id", userId)
        .neq("status", "pending");
      
      if (!repairsError && repairUpdates) {
        const unreadCount = repairsLastViewed 
          ? repairUpdates.filter(r => new Date(r.updated_at) > new Date(repairsLastViewed)).length
          : repairUpdates.length;
        setNewRepairsCount(unreadCount);
      }

      // Fetch unread user messages count
      const { count: unreadMsgsCount } = await supabase
        .from("customer_messages")
        .select("*", { count: 'exact', head: true })
        .eq("user_id", userId)
        .eq("is_read", false);
      setUnreadUserMessagesCount(unreadMsgsCount || 0);

      // Check if this is the first time visiting profile (from database preferences)
      const hasSeenTour = data.preferences && typeof data.preferences === 'object' && 
        'hasSeenProfileTour' in data.preferences ? 
        data.preferences.hasSeenProfileTour : false;
      if (!hasSeenTour) {
        setShowTour(true);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("שגיאה בטעינת הפרופיל");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTourComplete = async () => {
    setShowTour(false);
    
    // Save to database preferences
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const currentPreferences = profile?.preferences || {};
      await supabase
        .from("profiles")
        .update({
          preferences: {
            ...currentPreferences,
            hasSeenProfileTour: true
          }
        })
        .eq("id", session.user.id);
    } catch (error) {
      console.error("Error saving tour completion:", error);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const updates = {
        id: session.user.id,
        first_name: firstName,
        last_name: lastName,
        phone,
        birth_date: birthDate || null,
        shipping_address: {
          street: streetAddress,
          city,
          zip_code: zipCode,
        },
      };

      const { error } = await supabase
        .from("profiles")
        .upsert(updates);

      if (error) throw error;
      
      // Update profile state with new values
      await loadProfile(session.user.id);

      toast.success("הפרופיל עודכן בהצלחה!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("שגיאה בעדכון הפרופיל");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("הסיסמאות אינן תואמות");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("הסיסמה חייבת להכיל לפחות 6 תווים");
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success("הסיסמה שונתה בהצלחה!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("שגיאה בשינוי הסיסמה");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    // Preserve recently viewed products across logout
    const preservedRecentlyViewed = (() => {
      try {
        return localStorage.getItem('recentlyViewedProducts');
      } catch {
        return null;
      }
    })();

    const restorePreserved = () => {
      if (!preservedRecentlyViewed) return;
      try {
        localStorage.setItem('recentlyViewedProducts', preservedRecentlyViewed);
      } catch {
        // ignore
      }
    };

    try {
      // Clear local state first
      setProfile(null);
      setUserEmail("");
      setIsAdmin(false);

      // Clear localStorage (but keep recently viewed)
      localStorage.clear();
      restorePreserved();

      // Sign out from Supabase (using { scope: 'local' } to only clear local session)
      await supabase.auth.signOut({ scope: 'local' });

      toast.success("התנתקת בהצלחה");

      // Navigate to home
      navigate("/");

      // Force reload to clear any remaining state
      setTimeout(() => {
        window.location.href = "/";
      }, 100);
    } catch (error: any) {
      console.error("Error signing out:", error);
      // Even if there's an error, clear everything and navigate
      setProfile(null);
      setUserEmail("");
      setIsAdmin(false);
      localStorage.clear();
      restorePreserved();
      navigate("/");
      setTimeout(() => {
        window.location.href = "/";
      }, 100);
      toast.success("התנתקת בהצלחה");
    }
  };

  // Account links (for all users)
  const accountLinks = [
    {
      label: "פרטים אישיים",
      href: "#details",
      icon: <User className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "הזמנות שלי",
      href: "#orders",
      icon: <ShoppingBag className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "מועדפים",
      href: "#favorites",
      icon: <Heart className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
  ];

  // Tracking links (for all users)
  const trackingLinks = [
    {
      label: "מעקב תיקונים ומשלוחים",
      href: "#repairs",
      icon: (
        <div className="relative">
          <Bell className="text-foreground h-5 w-5" />
          {newRepairsCount > 0 && (
            <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium z-10">
              {newRepairsCount}
            </span>
          )}
        </div>
      ),
    },
    {
      label: "ההודעות שלי",
      href: "#messages",
      icon: (
        <div className="relative">
          <Mail className="text-foreground h-5 w-5" />
          {unreadUserMessagesCount > 0 && (
            <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium z-10">
              {unreadUserMessagesCount}
            </span>
          )}
        </div>
      ),
    },
  ];

  // Admin links (only for admins)
  const adminLinks = isAdmin ? [
    {
      label: "ניהול הודעות צור קשר",
      href: "#admin",
      icon: (
        <div className="relative">
          <MessageSquare className="text-foreground h-5 w-5" />
          {newMessagesCount > 0 && (
            <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium z-10">
              {newMessagesCount}
            </span>
          )}
        </div>
      ),
    },
    {
      label: "שליחת הודעות ללקוחות",
      href: "#customer-messages",
      icon: <Send className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול תיקונים והזמנות",
      href: "#admin-repairs",
      icon: <Wrench className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול משתמשים",
      href: "#users",
      icon: <Users className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "הגדרות תפריטים",
      href: "#settings",
      icon: <Settings className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול סליידר",
      href: "#slider",
      icon: <Image className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול שאלות נפוצות",
      href: "#faq",
      icon: <HelpCircle className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול פס מבצעים",
      href: "#promo-banner",
      icon: <Megaphone className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "עזרה וריאנטים",
      href: "#color-helper",
      icon: <Palette className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "הודעות SMS",
      href: "#sms-templates",
      icon: <MessageCircle className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "הגדרות חיפוש",
      href: "#search-settings",
      icon: <Search className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "הגדרות יועץ תכשיטים",
      href: "#bot-settings",
      icon: <Bot className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "תצוגת חנות (TV)",
      href: "#tv-display",
      icon: <Monitor className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "ניהול בלוג",
      href: "#blog-management",
      icon: <FileText className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
    {
      label: "דוח נגישות",
      href: "#accessibility",
      icon: <Accessibility className="text-foreground h-5 w-5 flex-shrink-0" />,
    },
  ] : [];

  // For tour anchors - combine all links
  const allLinks = [...accountLinks, ...trackingLinks, ...adminLinks];

  const markRepairsAsViewed = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const currentPreferences = profile?.preferences || {};
      await supabase
        .from("profiles")
        .update({
          preferences: {
            ...(typeof currentPreferences === 'object' ? currentPreferences : {}),
            repairs_last_viewed_at: new Date().toISOString()
          }
        })
        .eq("id", session.user.id);
      
      setNewRepairsCount(0);
    } catch (error) {
      console.error("Error marking repairs as viewed:", error);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "repairs") {
      markRepairsAsViewed();
    }
    if (isMobile) setMobileMenuOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="gold-bar-loader" />
        <span className="text-sm text-muted-foreground">טוען...</span>
      </div>
    );
  }

  const renderMenuSection = (title: string, links: typeof accountLinks, showDivider = false) => (
    <>
      {showDivider && (
        <div className="my-3 border-t border-border/50" />
      )}
      <div className="mb-3">
        <motion.span
          animate={{
            display: isMobile ? "block" : (open ? "block" : "none"),
            opacity: isMobile ? 1 : (open ? 1 : 0),
          }}
          className="text-xs text-muted-foreground px-2 mb-2 block"
        >
          {title}
        </motion.span>
        <div className="space-y-1">
          {links.map((link, idx) => (
            <button
              key={idx}
              id={`tour-anchor-${link.href.replace('#', '')}`}
              onClick={() => {
                handleTabChange(link.href.replace('#', '') as typeof activeTab);
              }}
              className={cn(
                "flex items-center gap-2 group/sidebar py-2 hover:bg-muted/50 rounded-md transition-colors overflow-visible w-full",
                isMobile ? "justify-start px-2" : (open ? "justify-start px-2" : "justify-center px-4"),
                activeTab === link.href.replace('#', '') && "bg-primary/10"
              )}
            >
              {link.icon}
              <motion.span
                animate={{
                  display: isMobile ? "inline-block" : (open ? "inline-block" : "none"),
                  opacity: isMobile ? 1 : (open ? 1 : 0),
                }}
                className="text-foreground text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
              >
                {link.label}
              </motion.span>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const MenuContent = () => (
    <div className="flex flex-col h-full px-3">
      {/* Logo/Greeting - Fixed at top */}
      <div className="flex-shrink-0">
        {!isMobile && (open ? <Logo firstName={profile?.first_name || ""} /> : <LogoIcon firstName={profile?.first_name || ""} lastName={profile?.last_name || ""} />)}
        {isMobile && <Logo firstName={profile?.first_name || ""} isLink={false} />}
      </div>
      
      {/* Menu sections - Scrollable */}
      <div
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden mt-8",
          !isMobile && !open && "scrollbar-hidden"
        )}
      >
        <div className="flex flex-col">
          {renderMenuSection("החשבון שלי", accountLinks)}
          {renderMenuSection("מעקב", trackingLinks, true)}
          {isAdmin && renderMenuSection("ניהול האתר", adminLinks, true)}
        </div>
      </div>
      
      {/* Divider before action buttons */}
      <div className="border-t border-border/50 pt-3 mt-3 space-y-2">
        <RouterLink to="/" onClick={() => isMobile && setMobileMenuOpen(false)}>
          <button
            className={cn(
              "flex items-center gap-2 group/sidebar py-2 hover:bg-muted/50 rounded-md transition-colors w-full",
              isMobile ? "justify-start px-2" : (open ? "justify-start px-2" : "justify-center px-2")
            )}
          >
            <Home className="text-foreground h-5 w-5 flex-shrink-0" />
            <motion.span
              animate={{
                display: isMobile ? "inline-block" : (open ? "inline-block" : "none"),
                opacity: isMobile ? 1 : (open ? 1 : 0),
              }}
              className="text-foreground text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
            >
              חזרה לאתר
            </motion.span>
          </button>
        </RouterLink>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isMobile) setMobileMenuOpen(false);
            setTimeout(() => {
              handleLogout();
            }, 100);
          }}
          className={cn(
            "flex items-center gap-2 group/sidebar py-2 hover:bg-destructive/10 rounded-md transition-colors w-full",
            isMobile ? "justify-start px-2" : (open ? "justify-start px-2" : "justify-center px-2")
          )}
        >
          <LogOut className="text-destructive h-5 w-5 flex-shrink-0" />
          <motion.span
            animate={{
              display: isMobile ? "inline-block" : (open ? "inline-block" : "none"),
              opacity: isMobile ? 1 : (open ? 1 : 0),
            }}
            className="text-destructive text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
          >
            התנתק
          </motion.span>
        </button>
      </div>
    </div>
  );

  return (
    <PageTransition>
      <SEO
        title="האזור האישי שלי"
        description="האזור האישי שלך באליס תכשיטים - ניהול הזמנות, מועדפים, מעקב תיקונים ועדכון פרטים אישיים"
        canonical="https://alice.jewelry/profile"
        noindex={true}
      />
      <div
        className={cn(
          "min-h-screen flex flex-col md:flex-row bg-background w-full overflow-hidden"
        )}
        dir="rtl"
      >
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sidebar open={open} setOpen={setOpen}>
            <SidebarBody className="justify-between gap-10 bg-muted/30 fixed right-0 top-0 h-screen z-50">
              <MenuContent />
            </SidebarBody>
          </Sidebar>
        )}

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <MobileProfileNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            newMessagesCount={newMessagesCount}
            newRepairsCount={newRepairsCount}
            unreadUserMessagesCount={unreadUserMessagesCount}
            onLogout={handleLogout}
            firstName={profile?.first_name || undefined}
          />
        )}

        <motion.div 
          className="flex flex-1 w-full"
          animate={{
            marginRight: isMobile ? 0 : (open ? "300px" : "60px")
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="p-4 md:p-10 bg-background flex flex-col gap-4 flex-1 w-full h-full overflow-auto pt-4 pb-20 md:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl md:text-3xl font-light text-foreground">
                {activeTab === "details" && "פרטים אישיים"}
                {activeTab === "orders" && "ההזמנות שלי"}
                {activeTab === "favorites" && "המועדפים שלי"}
                {activeTab === "repairs" && "מעקב תיקונים ומשלוחים"}
                {activeTab === "messages" && "ההודעות שלי"}
                {activeTab === "admin" && "ניהול הודעות צור קשר"}
                {activeTab === "customer-messages" && "שליחת הודעות ללקוחות"}
                {activeTab === "settings" && "הגדרות תפריטים"}
                {activeTab === "slider" && "ניהול סליידר"}
                {activeTab === "users" && "ניהול משתמשים"}
                {activeTab === "admin-repairs" && "ניהול תיקונים והזמנות"}
                {activeTab === "faq" && "ניהול שאלות נפוצות"}
                {activeTab === "promo-banner" && "ניהול פס מבצעים"}
                {activeTab === "color-helper" && "עזרה - צבעים"}
                {activeTab === "sms-templates" && "הודעות SMS"}
                {activeTab === "blog-management" && "ניהול בלוג"}
                {activeTab === "accessibility" && "דוח נגישות"}
              </h1>
            </div>

            {showTour && (
              <ProfileTour
                isAdmin={isAdmin}
                activeTab={activeTab}
                onComplete={handleTourComplete}
              />
            )}

            {activeTab === "details" && (
              <>
                <Card className="animate-fade-in">
                  <CardHeader>
                    <CardTitle className="text-right font-light">פרטי החשבון</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSave} className="space-y-6">
                      <div className="space-y-2">
                        <Label>אימייל</Label>
                        <Input value={userEmail} disabled className="text-right bg-muted" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="first-name">שם פרטי</Label>
                          <Input
                            id="first-name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="text-right"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last-name">שם משפחה</Label>
                          <Input
                            id="last-name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="text-right"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">טלפון</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="text-right"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="birth-date">תאריך לידה</Label>
                          <Input
                            id="birth-date"
                            type="date"
                            value={birthDate}
                            onChange={(e) => setBirthDate(e.target.value)}
                            className="text-right"
                          />
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h3 className="text-lg font-light mb-4">כתובת משלוח</h3>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="street">רחוב ומספר בית</Label>
                            <Input
                              id="street"
                              value={streetAddress}
                              onChange={(e) => setStreetAddress(e.target.value)}
                              className="text-right"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="city">עיר</Label>
                              <Input
                                id="city"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className="text-right"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="zip">מיקוד</Label>
                              <Input
                                id="zip"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                                className="text-right"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <Button type="submit" className="w-full" disabled={isSaving}>
                        {isSaving ? (
                          <>
                            <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                            שומר...
                          </>
                        ) : (
                          "שמור שינויים"
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card className="animate-fade-in mt-4">
                  <CardHeader>
                    <CardTitle className="text-right font-light">שינוי סיסמה</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-password">סיסמה חדשה</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="text-right"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">אישור סיסמה חדשה</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="text-right"
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={isSaving}>
                        {isSaving ? (
                          <>
                            <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                            משנה סיסמה...
                          </>
                        ) : (
                          "שנה סיסמה"
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </>
            )}

            {activeTab === "orders" && (
              <div className="animate-fade-in">
                <OrderHistory userEmail={userEmail} />
              </div>
            )}

            {activeTab === "favorites" && (
              <div className="animate-fade-in">
                <FavoritesSection />
              </div>
            )}

            {activeTab === "admin" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminContactMessages onNewMessagesCountChange={setNewMessagesCount} />
                </Suspense>
              </div>
            )}

            {activeTab === "settings" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminMenuSettings />
                </Suspense>
              </div>
            )}

            {activeTab === "slider" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminSliderSettings />
                </Suspense>
              </div>
            )}

            {activeTab === "users" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminUserManagement />
                </Suspense>
              </div>
            )}

            {activeTab === "faq" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminAllFAQSettings />
                </Suspense>
              </div>
            )}

            {activeTab === "repairs" && (
              <div className="animate-fade-in">
                <UserRepairOrders />
              </div>
            )}

            {activeTab === "messages" && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <UserMessages onUnreadCountChange={setUnreadUserMessagesCount} />
                </Suspense>
              </div>
            )}

            {activeTab === "admin-repairs" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminRepairManagement />
                </Suspense>
              </div>
            )}

            {activeTab === "promo-banner" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminPromoBannerSettings />
                </Suspense>
              </div>
            )}

            {activeTab === "color-helper" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminColorHelper />
                </Suspense>
              </div>
            )}

            {activeTab === "customer-messages" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminCustomerMessages />
                </Suspense>
              </div>
            )}

            {activeTab === "search-settings" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminSearchSettings />
                </Suspense>
              </div>
            )}


            {activeTab === "sms-templates" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminSmsTemplates />
                </Suspense>
              </div>
            )}

            {activeTab === "bot-settings" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminBotSettings />
                </Suspense>
              </div>
            )}

            {activeTab === "tv-display" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminTVDisplaySettings />
                </Suspense>
              </div>
            )}

            {activeTab === "blog-management" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminBlogManagement />
                </Suspense>
              </div>
            )}

            {activeTab === "accessibility" && isAdmin && (
              <div className="animate-fade-in">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                  <AdminAccessibilityDashboard />
                </Suspense>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
};

const Logo = ({ firstName, isLink = true }: { firstName: string; isLink?: boolean }) => {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "בוקר טוב";
    if (hour >= 12 && hour < 17) return "צהריים טובים";
    if (hour >= 17 && hour < 21) return "ערב טוב";
    return "לילה טוב";
  };

  const content = (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="font-light text-foreground text-xl whitespace-pre text-center"
    >
      {getGreeting()} {firstName}
    </motion.span>
  );

  if (!isLink) {
    return (
      <div className="font-normal flex gap-2 items-center justify-center text-sm py-1 relative z-20">
        {content}
      </div>
    );
  }

  return (
    <RouterLink
      to="/"
      className="font-normal flex gap-2 items-center justify-center text-sm py-1 relative z-20"
    >
      {content}
    </RouterLink>
  );
};

const LogoIcon = ({ firstName, lastName }: { firstName: string; lastName: string }) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'ת.א';
  
  return (
    <RouterLink
      to="/"
      className="font-normal flex gap-2 items-center justify-center text-sm py-1 relative z-20"
    >
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-light text-primary text-2xl"
      >
        {initials}
      </motion.span>
    </RouterLink>
  );
};

export default Profile;
